import { Link } from '@inertiajs/react'
import { type EditorRequest } from './revisionRequest'
import { type Section } from './rowAnchor'
import { TimeAgo } from '@/components/TimeAgo'
import { plan_top_down_path, plan_top_down_by_channel_path, plan_top_down_by_channel_quarterly_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'

const STEP: Record<Section, { name: string; href: (market: string, slug: string) => string }> = {
  overall: { name: 'Overall Plan', href: plan_top_down_path },
  channel: { name: 'Channel Breakdown', href: plan_top_down_by_channel_path },
  quarterly: { name: 'Channel Quarterly Breakdown', href: plan_top_down_by_channel_quarterly_path },
}

// On the three editor pages while a revision request is open: who asked,
// their message, how many comments per step (links to the other steps), and
// this step's comments listed (rows the PIC removed are named as gone).
export default function RevisionRequestBanner({ request, section, slug }: { request: EditorRequest; section: Section; slug: string }) {
  const market = useMarket()
  const others = (Object.keys(STEP) as Section[]).filter((s) => s !== section)
  return (
    <div className="mb-4 rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-foreground">
      <div>
        <span className="font-semibold">{request.requested_by ?? 'Someone'}</span> asked for changes
        <span className="text-muted-foreground"> · <TimeAgo at={request.requested_at} /></span>
      </div>
      <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed">{request.message}</p>
      <div className="mt-2 text-xs text-muted-foreground">
        {request.counts[section]} on this step
        {others.map((s) => (
          <span key={s}>
            {' · '}
            <Link href={STEP[s].href(market.slug, slug)} className="text-link hover:underline">{request.counts[s]} on {STEP[s].name}</Link>
          </span>
        ))}
      </div>
      {request.comments.length > 0 && (
        <ul className="mt-2 flex flex-col gap-1 text-[13px]">
          {request.comments.map((c) => (
            <li key={c.id}>
              <span className="font-semibold">{c.anchor_label}</span>
              {c.row_id == null && <span className="text-muted-foreground"> (row no longer in the plan)</span>}
              {': '}{c.body}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
